/* eslint-disable arrow-body-style */
import { debounce } from 'throttle-debounce';

import {
  NOTIFICATION_ADD_TO_CART_FREE_SHIPPING_LIMIT_EXCEEDED_CART,
  NOTIFICATION_ADD_TO_CART_FREE_SHIPPING_LIMIT_EXCEEDED_KT,
  NOTIFICATION_ADD_TO_CART_FREE_SHIPPING_LIMIT_NOT_EXCEEDED_CART,
  NOTIFICATION_ADD_TO_CART_FREE_SHIPPING_LIMIT_NOT_EXCEEDED_KT,
  NOTIFICATION_CHOOSE_ONE_WAY_FREE_SHIPPING_LIMIT_EXCEEDED_CART,
} from '@common/constants/features';

import {
  CHOICE_BEST_GROUP,
  DEFAULT_TEXTS,
  EXCEEDED,
  NOT_EXCEEDED,
  PLACEHOLDER_MAP,
} from '@/constants/free-shipping-notifications';
import {
  TRIGGER_CHOICE_BEST_SHIPPING_GROUP_PROP,
  TRIGGER_FREE_SHIPPING_LIMIT_EXCEEDED_PROP,
  TRIGGER_FREE_SHIPPING_LIMIT_NOT_EXCEEDED_PROP,
} from '@/constants/settings';
import {
  bestWayNotification,
  freeShippingNotification,
  paidShippingNotification,
} from '@/gtm/trigger-notifications';
import { createShippingStorage } from '@/services/storage/shipping-storage';
import { price } from '@/utils/filters/price';

const getDeliveryGroup = (shippingGroups = []) => {
  const shippingGroupsFiltered = shippingGroups.filter(({ type }) => !['soldout'].includes(type));
  const deliveryGroup = shippingGroupsFiltered.find(({ type }) => ['delivery', 'thirdparty'].includes(type));

  return { deliveryGroup, shippingGroupsFiltered };
};

const canShowNotExceededNotification = ({
  shippingGroups, shippingStorage, isFeatureEnabled, deliveryGroup,
}) => {
  // недоступна бесплатная доставка - не показываем
  if (!deliveryGroup?.freeShippingLimit) return false;

  const oldValues = shippingStorage.getValues();
  const shippingPrice = parseInt(deliveryGroup?.subtotal?.shippingPrice) || 0;

  // цена не изменилась - ничего не показываем (при переходе в корзину чтоб ничего не показывалось)
  const isTotalPriceChanged = oldValues.totalPrice !== deliveryGroup?.subtotal?.totalPrice;
  const oneShippingGroup = shippingGroups.length === 1;
  const isFreeShipping = shippingPrice === 0;

  return oneShippingGroup
    && !isFreeShipping
    && isTotalPriceChanged
    && deliveryGroup
    && isFeatureEnabled;
};

const canShowExceededNotification = ({
  shippingGroups, shippingStorage, isFeatureEnabled, deliveryGroup,
}) => {
  const oldValues = shippingStorage.getValues();
  const shippingPrice = parseInt(deliveryGroup?.subtotal?.shippingPrice) || 0;
  // если уже ранее достигнуто условие для бесплатной доставки повторно не показываем
  const isAlreadyFreeShipping = oldValues.shippingPrice === 0 && shippingPrice === 0;
  // цена не изменилась - ничего не показываем (при переходе в корзину чтоб ничего не показывалось)
  const isTotalPriceChanged = oldValues.totalPrice !== deliveryGroup?.subtotal?.totalPrice;
  const oneShippingGroup = shippingGroups.length === 1;
  const isFreeShipping = shippingPrice === 0;

  return oneShippingGroup
    && !isAlreadyFreeShipping
    && isTotalPriceChanged
    && deliveryGroup
    && isFreeShipping
    && oldValues.shippingGroupsCount === 1
    && isFeatureEnabled;
};

const canShowChoiceBestShippingNotification = ({ shippingGroups, shippingStorage, isFeatureEnabled }) => {
  const oldValues = shippingStorage.getValues();
  const shippingPrice = parseInt(shippingGroups?.[0]?.subtotal?.shippingPrice) || 0;
  const oneShippingGroup = shippingGroups.length === 1;
  const isFreeShipping = shippingPrice === 0;
  const isNotUnallocated = shippingGroups?.[0]?.type !== 'unallocated';

  return oneShippingGroup
    && isNotUnallocated
    && isFreeShipping
    && oldValues.shippingGroupsCount > 1
    && isFeatureEnabled;
};

export default {
  namespaced: true,

  state: () => ({
    [NOT_EXCEEDED]: {
      title: DEFAULT_TEXTS[NOT_EXCEEDED].title,
      text: DEFAULT_TEXTS[NOT_EXCEEDED].text,
    },
    [EXCEEDED]: {
      title: DEFAULT_TEXTS[EXCEEDED].title,
      text: DEFAULT_TEXTS[EXCEEDED].text,
    },
    [CHOICE_BEST_GROUP]: {
      title: DEFAULT_TEXTS[CHOICE_BEST_GROUP].title,
      text: DEFAULT_TEXTS[CHOICE_BEST_GROUP].text,
    },
  }),

  mutations: {
    setText(state, { texts, key }) {
      const stateKey = {
        [TRIGGER_FREE_SHIPPING_LIMIT_NOT_EXCEEDED_PROP]: NOT_EXCEEDED,
        [TRIGGER_FREE_SHIPPING_LIMIT_EXCEEDED_PROP]: EXCEEDED,
        [TRIGGER_CHOICE_BEST_SHIPPING_GROUP_PROP]: CHOICE_BEST_GROUP,
      }[key];

      if (!stateKey) {
        throw new Error('Нет такого ключа');
      }

      state[stateKey].title = texts.title;
      state[stateKey].text = texts.text;
    },
  },

  getters: {
    isNotExceededNotificationFeatureEnabled: (state, getters) => (routeName) => {
      return getters.isNotificationFeatureEnabled({
        cartKey: NOTIFICATION_ADD_TO_CART_FREE_SHIPPING_LIMIT_NOT_EXCEEDED_CART,
        ktKey: NOTIFICATION_ADD_TO_CART_FREE_SHIPPING_LIMIT_NOT_EXCEEDED_KT,
        routeName,
      });
    },
    isExceededNotificationFeatureEnabled: (state, getters) => (routeName) => {
      return getters.isNotificationFeatureEnabled({
        cartKey: NOTIFICATION_ADD_TO_CART_FREE_SHIPPING_LIMIT_EXCEEDED_CART,
        ktKey: NOTIFICATION_ADD_TO_CART_FREE_SHIPPING_LIMIT_EXCEEDED_KT,
        routeName,
      });
    },
    // eslint-disable-next-line max-params
    isNotificationFeatureEnabled: (state, getters, rootState, rootGetters) => ({ cartKey, ktKey, routeName }) => {
      if (routeName === 'Checkout') {
        return rootGetters['features/$isFeatureEnabled'](cartKey);
      }

      return rootGetters['features/$isFeatureEnabled'](ktKey);
    },
    // eslint-disable-next-line max-params
    isChoiceBestShippingNotificationFeatureEnabled: (state, getters, rootState, rootGetters) => rootGetters['features/$isFeatureEnabled'](NOTIFICATION_CHOOSE_ONE_WAY_FREE_SHIPPING_LIMIT_EXCEEDED_CART),
    shippingStorage() {
      return createShippingStorage();
    },
  },

  actions: {
    // debounce чтоб не показывать 2 раза уведомление
    // при редактировании размера товара(добавляем новый и удаляем старый)
    showNotification: debounce(1000, ({ dispatch, state } = {}, {
      key, value, onShown, onClose,
    } = {}) => {
      dispatch('notifications/create', {
        type: key === NOT_EXCEEDED ? 'warning' : 'success',
        title: state[key].title,
        message: state[key].text.replace(PLACEHOLDER_MAP[key], value),
        onShown,
        onClose,
      }, { root: true });
    }),

    trackItems({ dispatch, getters }, { cartResponse, routeName }) {
      // все фичи отключены нет смысла дальше выполнять экшн
      if (!getters.isNotExceededNotificationFeatureEnabled(routeName)
        && !getters.isExceededNotificationFeatureEnabled(routeName)
        && !getters.isChoiceBestShippingNotificationFeatureEnabled) return;
      // пустой ответ
      if (!cartResponse || !Object.keys(cartResponse).length) return;

      const { deliveryGroup, shippingGroupsFiltered } = getDeliveryGroup(cartResponse.shippingGroups);

      const canToShowNotExceededNotification = canShowNotExceededNotification({
        deliveryGroup,
        shippingGroups: shippingGroupsFiltered,
        shippingStorage: getters.shippingStorage,
        isFeatureEnabled: getters.isNotExceededNotificationFeatureEnabled(routeName),
      });

      const canToShowExceededNotification = canShowExceededNotification({
        deliveryGroup,
        shippingGroups: shippingGroupsFiltered,
        shippingStorage: getters.shippingStorage,
        isFeatureEnabled: getters.isExceededNotificationFeatureEnabled(routeName),
      });

      const isChoiceBestShippingNotification = canShowChoiceBestShippingNotification({
        shippingGroups: shippingGroupsFiltered,
        shippingStorage: getters.shippingStorage,
        isFeatureEnabled: getters.isChoiceBestShippingNotificationFeatureEnabled,
      }) && routeName === 'Checkout';

      if (isChoiceBestShippingNotification) {
        dispatch('showNotification', {
          key: CHOICE_BEST_GROUP,
          onShown: bestWayNotification.onShown.bind(bestWayNotification),
          onClose: bestWayNotification.onClose.bind(bestWayNotification),
        });
      } else if (canToShowNotExceededNotification) {
        const { freeShippingLimit } = deliveryGroup;
        const { totalPriceWithoutShippingPrice } = deliveryGroup.subtotal;
        const moneyToFreeShipping = freeShippingLimit - totalPriceWithoutShippingPrice;
        dispatch('showNotification', {
          key: NOT_EXCEEDED,
          value: price(moneyToFreeShipping),
          onShown: paidShippingNotification.onShown.bind(paidShippingNotification),
          onClose: paidShippingNotification.onClose.bind(paidShippingNotification),
        });
      } else if (canToShowExceededNotification) {
        dispatch('showNotification', {
          key: EXCEEDED,
          value: deliveryGroup.products.length,
          onShown: freeShippingNotification.onShown.bind(freeShippingNotification),
          onClose: freeShippingNotification.onClose.bind(freeShippingNotification),
        });
      }
    },

    init({ dispatch, getters }, routeName) {
      dispatch('loadNotifications', TRIGGER_FREE_SHIPPING_LIMIT_EXCEEDED_PROP);
      dispatch('loadNotifications', TRIGGER_FREE_SHIPPING_LIMIT_NOT_EXCEEDED_PROP);
      dispatch('loadNotifications', TRIGGER_CHOICE_BEST_SHIPPING_GROUP_PROP);

      // срабатывает до изменения данных
      this.subscribe((mutation) => {
        if (mutation.type === 'cart/setCart') {
          dispatch('trackItems', {
            cartResponse: mutation.payload,
            routeName,
          });
        }
      });

      // срабатывает после изменения данных
      this.watch((state) => state.cart.shippingGroups, (shippingGroups) => {
        const { deliveryGroup, shippingGroupsFiltered } = getDeliveryGroup(shippingGroups);
        // не обновляем если это данные для миникорзины
        if (shippingGroupsFiltered?.[0]?.type === 'hidden') return;

        getters.shippingStorage.setValues({
          shippingPrice: parseInt(deliveryGroup?.subtotal?.shippingPrice),
          shippingGroupsCount: shippingGroupsFiltered.length,
          totalPrice: deliveryGroup?.subtotal.totalPrice,
        });
      }, {
        deep: true,
      });
    },

    async loadNotifications({ commit, rootGetters }, key) {
      const keyNotExists = ![TRIGGER_FREE_SHIPPING_LIMIT_EXCEEDED_PROP,
        TRIGGER_FREE_SHIPPING_LIMIT_NOT_EXCEEDED_PROP,
        TRIGGER_CHOICE_BEST_SHIPPING_GROUP_PROP].includes(key);
      if (keyNotExists) {
        throw new Error('Нет такого ключа');
      }

      try {
        const result = rootGetters['settings/getSettingByPath'](key);

        commit('setText', {
          key,
          texts: {
            title: result?.title,
            text: result?.text,
          },
        });
      } catch (e) {
        this.$logger.error(e);
      }
    },
  },
};
